import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Currency CRUD page in a tab.
 */
const CURRENCY_CRUD_ROUTE = "/home/currencies/currencyConfigCrud";

//endregion

//region Component

/**
 * Renders the Currencies configuration page.
 *
 * Responsibilities:
 * - render the common configuration-page layout;
 * - keep the page visually aligned with other Config pages through config-page.css;
 * - render the Currencies MTable;
 * - use the Locations permission module;
 * - expose only the View workflow for Currencies.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Currencies configuration page.
 */
export default function CurrencyConfig({ roles, onCreateTab, toastMessages }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this configuration page.
     *
     * Important:
     * - Currencies are treated as read-only here;
     * - MTable uses "Update" internally for the Edit action, so "Update" must be
     *   removed instead of "Edit";
     * - Audit is hidden to keep this page focused on the View workflow only.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Create", "Update", "Delete", "Audit"],
        []
    );

    /**
     * Opens the Currency CRUD tab for the requested operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected currency row when required;
     * - opens the configured CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected currency row.
     * @returns {void}
     */
    const openCurrencyCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (["Edit", "Copy", "View"].includes(operation)) {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "CurrencyLine",
                        value: row,
                    })
                );
            }

            const safeName = row?.Name ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewCurrency")
                    : operation === "View"
                        ? `${t("LabelViewCurrency")} "${safeName}"`
                        : `${t("LabelEditCurrency")} "${safeName}"`;

            onCreateTab(CURRENCY_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Currency workflow.
     *
     * Only View is intentionally exposed for this page.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openCurrencyCrud("View", row)],
            ]),
        [openCurrencyCrud]
    );

    return (
        <div
            className="config-page"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("Currencies")}
                </div>

                <div className="config-page__description">
                    {t("InfoCurrencies")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Currencies"
                    roleName="Locations"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    replaceFunctions={replaceFunctions}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

CurrencyConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion